/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Eye,
  Copy,
  Pencil,
  ReplyAllFinal,
  Check,
  Phone,
} from '@saleshandy/icons';
import { ImageIcon } from '@saleshandy/design-system';
import { parsePhoneNumber } from 'libphonenumber-js';
import { ProspectProfileFieldInstance } from '../helpers/prospect-fields';
import { Mail } from '../../../../../../../../shared/svg/mail';
import { Images } from '../../../../../../../../shared/app-constants';
import {
  OverlayPopover,
  OverlayTooltip,
} from '../../../../../../../../shared/design-system/components/overlay';
import { PinBlue } from '../../../../../../../../shared/svg/pin-blue';
import { UnpinGrey } from '../../../../../../../../shared/svg/unpin-grey';

import { TaskGrey } from '../../../../../../../../shared/svg/task';
import TaskCheckBox from '../../../../../../../../shared/svg/task-check-box';
import { SentEmail } from '../../../../../../../../shared/svg/sent-email';
import {
  accessibleOnClick,
  executeOnRequestStatus,
  getIsRequestPending,
} from '../../../../../../../../shared/utils';
import { useCopyToClipboard } from '../../../../../../../../shared/hooks/use-copy-to-clipboard';
import { getNodeText } from '../helpers/prospect-field-renderers';
import hasPermission from '../../../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../../../shared/utils/access-control/enums/permissions';
import { ProspectProfileSectionProps } from './types';
import ProspectProfileSectionSkeleton from './prospect-profile-section-skeleton';
import { Placement } from '../../../../../../../../shared/design-system/components/overlay-taget/enums';
import TruncatedTextWithAutoTooltip from '../../../../../../../../shared/components/truncated-text-with-auto-tooltip';
import {
  setAddClickedFieldId,
  setEmailFieldHovered,
} from '../../../../../../prospect-slice';
import { ProspectProfileHeaderBlock } from './prospect-profile-header-block';
import { setSelectedCallbackProspect } from '../../../../../../../dialer/dialer-slice';
import {
  CallLog,
  DialerCallSourceEnum,
} from '../../../../../../../dialer/types/call-logs';
import { isOngoingDialerCall } from '../../../../../../../../shared/components/dialer/constants';

const IMAGE_ICON_STYLE = [
  {
    label: 'task',
    ToolTip: 'Add Note',
    icon: <TaskGrey width={20} height={20} />,
    tab: 'notes',
  },
  {
    label: 'call',
    ToolTip: 'Make a Call',
    icon: <Phone width={20} height={20} />,
    action: 'call',
  },
  {
    label: 'mail',
    ToolTip: 'Create an Email',
    icon: <Mail width={20} height={20} />,
    tab: 'email',
  },
  {
    label: 'task-checkbox',
    ToolTip: 'Add a Task',
    icon: <TaskCheckBox width={20} height={20} />,
    tab: 'tasks',
  },
  {
    label: 'SentEmail',
    ToolTip: 'Add to Sequence',
    icon: <SentEmail width={20} height={20} />,
    action: 'sequence',
  },
];

export const ProspectProfileSection: React.FC<ProspectProfileSectionProps> = ({
  filteredProspectProfileFields,
  pinnedFieldIds,
  editingFieldId,
  onPinToggle,
  onPinKeyDown,
  onEditClick,
  onEditKeyDown,
  renderAddAction,
  handleOpenTagModal = () => {},
  dropDownActions,
  selectedProspectId,

  singleProspectResponse,
  getSingleProspectDetailsRequests,
  getCustomOutcomeRequests,
  setActiveTab,
  changeTab,
  activeTab,
  getPrimaryEmailChangeInProspectRequestStatus,
  getUpdatePrimaryNumberRequestStatus,
  singleProspectDetailsRequestStatus,
  fetchSingleProspectSequencesDetails,
  addTagsToProspectsRequest,
  onTaskIconClick,
  onEmailIconClick,
  renderAddTag,
  removeTagsFromProspectsRequestStatus,
  onAddSequenceExpanded,
  onActivityChange,
  isDialerFeatureEnabled,
  isAgencyUser,
  isCallQuickActionDisabled = false,
}) => {
  const dispatch = useDispatch();
  const currentCallManagementState = useSelector(
    (state: any) => state?.dialer?.currentCallManagementState,
  );
  const isOngoingCall = isOngoingDialerCall({
    callIsInprogress: currentCallManagementState?.callIsInprogress,
    callStatus: currentCallManagementState?.callStatus,
  });
  const canMakeCall =
    isDialerFeatureEnabled &&
    !isAgencyUser &&
    hasPermission(Permissions.PHONE_CALL_WRITE);
  const { copyToClipboard } = useCopyToClipboard();
  const [copiedFieldId, setCopiedFieldId] = useState<string | null>(null);
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const prospectTags = singleProspectResponse?.tags;

  const cleanCopiedText = (text: string) => {
    if (!text) return '';
    return (
      text
        // remove "+n more"
        ?.replace(/\s*\+\d+\s*more\s*$/i, '')
        // remove ALL currency symbols (Unicode-safe)
        ?.replace(/\p{Sc}/gu, '')
        // remove currency codes only near numbers (CHF, USD, INR)
        ?.replace(/\b[A-Z]{3}(?=\s*\d)|(?<=\d)\s*[A-Z]{3}\b/g, '')
        // normalize spaces
        ?.replace(/\s+/g, ' ')
        ?.trim()
    );
  };

  const getFieldValueForCopy = useCallback(
    (field: ProspectProfileFieldInstance): string => {
      let nodeContent = '';

      // Get prospect data
      const prospectData = Array.isArray(singleProspectResponse)
        ? singleProspectResponse[0]
        : singleProspectResponse;

      // For email field, get the primary email directly from attributes
      if (field?.id === 'email' && prospectData?.attributes) {
        const emailAttrs = prospectData.attributes?.filter(
          (item: any) => item?.fieldRef?.label === 'Email',
        );
        if (emailAttrs?.length > 0) {
          const primaryEmailObj = emailAttrs[0]?.attributeValue?.filter(
            (item: any) => item?.isPrimary,
          );
          const primaryEmail = primaryEmailObj?.[0]?.email;
          if (primaryEmail) {
            nodeContent = primaryEmail;
          }
        }
      }

      // For prospect-overview, check attributes directly
      if (field?.id === 'prospect-overview' && prospectData?.attributes) {
        const attr = prospectData.attributes.find(
          (a: any) => a?.fieldRef?.label?.trim() === 'Prospect Overview',
        );
        if (attr?.attributeValue) {
          nodeContent = String(attr.attributeValue);
        }
      }

      // For custom fields, try to get value from prospect attributes
      if (!nodeContent) {
        const customField = (field as any)?.customFieldData;
        if (customField && prospectData?.attributes) {
          const attr = prospectData.attributes.find(
            (a: any) =>
              a?.fieldRef?.label?.trim() === field?.label?.trim() ||
              String(a?.fieldRef?.id) === String(field?.id),
          );

          if (attr?.attributeValue) {
            nodeContent = String(attr.attributeValue);
          }
        }
      }

      // Fall back to extracting from valueNode if we don't have a value yet
      if (!nodeContent) {
        nodeContent = getNodeText(
          field?.valueNode ?? field?.value ?? field?.searchValue ?? '',
        ).trim();
      }

      return nodeContent;
    },
    [singleProspectResponse],
  );

  const handleCopyField = useCallback(
    (field: ProspectProfileFieldInstance) => {
      const nodeContent = getFieldValueForCopy(field);
      const cleanedContent = cleanCopiedText(nodeContent);

      if (!cleanedContent) return;

      // Clear any existing timeout
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }

      // Set the copied field ID to show check icon
      setCopiedFieldId(field.id);
      copyToClipboard(cleanedContent);

      // Set timeout to clear the check icon after 3 seconds
      copyTimeoutRef.current = setTimeout(() => {
        setCopiedFieldId(null);
        copyTimeoutRef.current = null;
      }, 3000);
    },
    [copyToClipboard, getFieldValueForCopy],
  );

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      copyTimeoutRef.current && clearTimeout(copyTimeoutRef.current);
    },
    [],
  );

  useEffect(() => {
    fetchSingleProspectSequencesDetails(selectedProspectId);
    getCustomOutcomeRequests();
  }, []);

  // Handler to open contacts tab when add icon is clicked
  const handleAddActionClick = useCallback(
    (fieldId?: string) => {
      dispatch(setAddClickedFieldId(fieldId));
      setActiveTab?.('contacts');
      if (changeTab && selectedProspectId) {
        changeTab(selectedProspectId, 'contacts');
      }
    },
    [setActiveTab, changeTab, selectedProspectId],
  );

  // Allow double click on a field value to trigger edit (when editable)
  const handleFieldDoubleClick = useCallback(
    (fieldId: string, canEdit?: boolean) => {
      if (canEdit) {
        onEditClick(fieldId);
      }
    },
    [onEditClick],
  );

  // Wrapper for renderAddAction that adds click handler to open contacts tab
  const renderAddActionWithClick = useCallback(
    (fieldId?: string) => {
      const addActionElement = renderAddAction();
      return (
        <span
          className="cursor-pointer"
          {...accessibleOnClick(() => handleAddActionClick(fieldId))}
        >
          {addActionElement}
        </span>
      );
    },
    [renderAddAction, handleAddActionClick],
  );
  useEffect(() => {
    executeOnRequestStatus({
      status: getPrimaryEmailChangeInProspectRequestStatus,
      onSuccess: () => {
        getSingleProspectDetailsRequests(selectedProspectId);
        getCustomOutcomeRequests();
        onActivityChange?.(true);
      },
    });
  }, [getPrimaryEmailChangeInProspectRequestStatus, onActivityChange]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getUpdatePrimaryNumberRequestStatus,
      onSuccess: () => {
        getSingleProspectDetailsRequests(selectedProspectId);
        getCustomOutcomeRequests();
        onActivityChange?.(true);
      },
    });
  }, [getUpdatePrimaryNumberRequestStatus, onActivityChange]);

  useEffect(() => {
    executeOnRequestStatus({
      status: addTagsToProspectsRequest,
      onSuccess: () => {
        getSingleProspectDetailsRequests(selectedProspectId);
        getCustomOutcomeRequests();
        onActivityChange?.(true);
      },
    });
  }, [addTagsToProspectsRequest, onActivityChange]);

  useEffect(() => {
    executeOnRequestStatus({
      status: removeTagsFromProspectsRequestStatus,
      onSuccess: () => {
        getSingleProspectDetailsRequests(selectedProspectId);
        getCustomOutcomeRequests();
        onActivityChange?.(true);
      },
    });
  }, [removeTagsFromProspectsRequestStatus, onActivityChange]);

  // Normalize singleProspectResponse to always be an array
  // eslint-disable-next-line no-nested-ternary
  const prospectData = Array.isArray(singleProspectResponse)
    ? singleProspectResponse
    : singleProspectResponse
    ? [singleProspectResponse]
    : [];

  if (getIsRequestPending(singleProspectDetailsRequestStatus)) {
    return <ProspectProfileSectionSkeleton />;
  }
  return (
    <>
      {prospectData.length > 0 ? (
        prospectData.map((item) => (
          <div
            key={item?.id || 'prospect'}
            className="prospect-profile-section-container"
          >
            <div className="prospect-profile-section-header-container">
              <ProspectProfileHeaderBlock
                item={item}
                dropDownActions={dropDownActions}
                dropdownId={`prospect-profile-dropdown-${
                  item?.id ?? 'prospect'
                }`}
              />
              {prospectTags?.length > 0 && (
                <div className="prospect-profile-section-header-user-tags">
                  {prospectTags?.slice(0, 2)?.map((tag: any) => (
                    <TruncatedTextWithAutoTooltip
                      key={tag?.id}
                      text={tag?.name}
                      className="prospect-tag-content regular-2"
                    />
                  ))}
                  {prospectTags?.length > 2 && (
                    <OverlayPopover
                      className="prospect-tag-popover"
                      content={
                        <div className="overlay-popover-content-wrap">
                          {prospectTags?.slice(2)?.map((tag: any) => (
                            <div className="text-wrap">
                              <TruncatedTextWithAutoTooltip
                                text={tag?.name}
                                className="prospect-tag regular-2"
                              />
                            </div>
                          ))}
                        </div>
                      }
                      placement={Placement.Bottom}
                      rootClose
                    >
                      <span className="prospect-tag-content cursor-pointer">
                        +{prospectTags?.length - 2}
                      </span>
                    </OverlayPopover>
                  )}
                </div>
              )}
              <div className="prospect-profile-section-header-tabs">
                {IMAGE_ICON_STYLE.map((option, index) => {
                  const isCallAction = option.action === 'call';
                  // Hide call button entirely if user doesn't have dialer access
                  if (isCallAction && !canMakeCall) return null;
                  const contactInfo =
                    item?.prospectContactInfo || item?.attributes;
                  const hasPhone =
                    !isCallAction ||
                    (contactInfo?.some(
                      (attr: any) =>
                        (attr?.type === 'phone' && attr?.contactInfo) ||
                        (attr?.fieldRef?.label === 'Phone' &&
                          attr?.attributeValue?.[0]?.phone),
                    ) ??
                      false);

                  const isCallControlDisabled =
                    isCallAction &&
                    (!hasPhone || isCallQuickActionDisabled || isOngoingCall);

                  return (
                    <OverlayTooltip
                      key={`${index + 1}`}
                      text={
                        isCallAction && !hasPhone
                          ? ' No phone number available. Please add a phone number to call a prospect.'
                          : isCallAction && isCallQuickActionDisabled
                          ? 'Call unavailable'
                          : isCallAction && isOngoingCall
                          ? 'Another call is already in progress'
                          : option.ToolTip
                      }
                    >
                      <div
                        className={`prospect-profile-section-header-tab-item${
                          isCallControlDisabled ? ' disabled' : ''
                        }`}
                        {...accessibleOnClick(() => {
                          if (option.tab) {
                            setActiveTab?.(option.tab);
                            // Update URL when clicking tabs
                            if (changeTab && selectedProspectId) {
                              changeTab(selectedProspectId, option.tab as any);
                            }
                            // If clicking on tasks tab, trigger callback to open add task form
                            if (option.tab === 'tasks' && onTaskIconClick) {
                              onTaskIconClick();
                            }
                            // If clicking on email tab, trigger callback to open email editor
                            if (option.tab === 'email' && onEmailIconClick) {
                              onEmailIconClick();
                            }
                            return;
                          }

                          // 🔹 Make a Call
                          if (option.action === 'call') {
                            setActiveTab?.('calls');
                            if (
                              !hasPhone ||
                              isCallQuickActionDisabled ||
                              isOngoingCall
                            ) {
                              return;
                            }
                            // Blur to dismiss tooltip when dialer PIP opens
                            (document.activeElement as HTMLElement)?.blur();
                            const prospectContactInfo =
                              item?.prospectContactInfo || item?.attributes;
                            const phoneItems =
                              prospectContactInfo?.filter(
                                (attr: any) =>
                                  attr?.type === 'phone' ||
                                  attr?.fieldRef?.label === 'Phone',
                              ) || [];
                            const primaryPhoneId =
                              item?.primaryPhoneProspectContactInfoId;
                            const primaryPhoneObj = phoneItems.find(
                              (p: any) => p.id === primaryPhoneId,
                            );
                            const phoneNumber =
                              primaryPhoneObj?.contactInfo ||
                              primaryPhoneObj?.attributeValue?.[0]?.phone ||
                              phoneItems[0]?.contactInfo ||
                              phoneItems[0]?.attributeValue?.[0]?.phone ||
                              '';

                            if (phoneNumber) {
                              let callingCode = '';
                              let localNumber = phoneNumber;

                              try {
                                const parsed = parsePhoneNumber(phoneNumber);
                                if (parsed) {
                                  callingCode =
                                    parsed.countryCallingCode?.toString() || '';
                                  localNumber =
                                    parsed.nationalNumber?.toString() ||
                                    phoneNumber;
                                }
                              } catch {
                                // If parsing fails, use the full number without calling code
                              }
                              if (!callingCode) {
                                callingCode = '1';
                              }
                              dispatch(
                                setSelectedCallbackProspect([
                                  {
                                    callSource: DialerCallSourceEnum.CRM,
                                    prospect: {
                                      id: item?.id,
                                      firstName: item?.firstName,
                                      lastName: item?.lastName,
                                    },
                                    prospectPhone: {
                                      callingCode,
                                      number: localNumber,
                                      phoneNumber: localNumber,
                                    },
                                    prospectPhoneNumberList:
                                      item?.prospectContactInfo
                                        ?.map((i) => {
                                          if (i?.type !== 'phone') return null;
                                          const contact = i?.contactInfo;
                                          if (!contact) return null;
                                          try {
                                            const parsed = parsePhoneNumber(
                                              contact,
                                              'US',
                                            );
                                            return {
                                              id: i?.id,
                                              phoneNumber:
                                                parsed.nationalNumber?.toString() ||
                                                contact,
                                              number: contact,
                                              callingCode:
                                                parsed.countryCallingCode?.toString() ||
                                                '1',
                                            };
                                          } catch {
                                            return {
                                              id: i?.id,
                                              phoneNumber: contact,
                                              number: contact,
                                              callingCode: '1',
                                            };
                                          }
                                        })
                                        ?.filter(Boolean) || [],
                                  } as CallLog,
                                ]),
                              );
                            }
                            return;
                          }

                          // 🔹 Tag Modal
                          if (
                            option.action === 'tags' &&
                            hasPermission(
                              Permissions.SEQUENCE_PROSPECT_UPDATE_TAGS,
                            )
                          ) {
                            handleOpenTagModal();
                            return;
                          }

                          // 🔹 Open Sequences Tab
                          if (
                            option.action === 'sequence' &&
                            hasPermission(
                              Permissions.SEQUENCE_PROSPECT_ADD_TO_SEQUENCE,
                            )
                          ) {
                            setActiveTab?.('sequences');
                            // Update URL when opening sequences tab
                            if (changeTab && selectedProspectId) {
                              changeTab(selectedProspectId, 'sequences');
                            }
                            // If clicking on sequences tab, trigger callback to expand add sequence form
                            if (
                              option.action === 'sequence' &&
                              onAddSequenceExpanded
                            ) {
                              onAddSequenceExpanded();
                            }
                          }
                        })}
                      >
                        {option.icon}
                      </div>
                    </OverlayTooltip>
                  );
                })}
                <div className="d-flex" tabIndex={0} role="button">
                  {renderAddTag()}
                </div>
              </div>
            </div>

            <div className="prospect-profile-section-body-container">
              <div className="prospect-profile-section-count-container">
                <div className="prospect-profile-section-count">
                  <span>
                    {item?.engagementFields?.numberOfEmailsSent ??
                      item?.numberOfEmailsSent ??
                      0}
                  </span>
                  <OverlayTooltip text="Total emails sent to this prospect.">
                    <span className="prospect-profile-section-count-action">
                      <Mail width={12} height={12} /> Sent
                    </span>
                  </OverlayTooltip>
                </div>
                <span className="prospect-profile-section-divider" />
                <div className="prospect-profile-section-count">
                  <span>
                    {item?.engagementFields?.numberOfOpens ??
                      item?.numberOfOpens ??
                      0}
                  </span>
                  <OverlayTooltip text="Number of emails this prospect has opened.">
                    <span className="prospect-profile-section-count-action">
                      <Eye width={12} height={12} /> Opened
                    </span>
                  </OverlayTooltip>
                </div>
                <span className="prospect-profile-section-divider" />
                <div className="prospect-profile-section-count">
                  <span>
                    {item?.engagementFields?.numberOfClicks ??
                      item?.numberOfClicks ??
                      0}
                  </span>
                  <OverlayTooltip text="Number of times this prospect clicked a link in your emails.">
                    <span className="prospect-profile-section-count-action">
                      <ImageIcon
                        src={Images.ClickedDisabled}
                        alt="click"
                        width={12}
                        height={12}
                      />{' '}
                      Clicked
                    </span>
                  </OverlayTooltip>
                </div>
                <span className="prospect-profile-section-divider" />
                <div className="prospect-profile-section-count">
                  <span>
                    {item?.engagementFields?.numberOfReplies ??
                      item?.numberOfReplies ??
                      0}
                  </span>
                  <OverlayTooltip text="Number of replies received from this prospect.">
                    <span className="prospect-profile-section-count-action">
                      <ReplyAllFinal width={12} height={12} /> Replied
                    </span>
                  </OverlayTooltip>
                </div>
                {/* <span className="prospect-profile-section-divider" /> */}
                {/* <div className="prospect-profile-section-count">
                  <span>1</span>
                  <OverlayTooltip text="Number of calls made to this prospect.">
                    <span className="prospect-profile-section-count-action">
                      <CallGrey width={12} height={12} /> Calls
                    </span>
                  </OverlayTooltip>
                </div> */}
              </div>

              <div className="prospect-profile-section-email-field-interaction">
                {filteredProspectProfileFields?.map((field) => {
                  if (field.variant === 'search') {
                    return (
                      <div
                        className="prospect-profile-section-fields"
                        key={field.id}
                      >
                        <div className="prospect-profile-section-input-container">
                          <div className="prospect-profile-section-field-detail">
                            {field.valueNode}
                          </div>
                          <div className="prospect-profile-section-action-btn">
                            {field.showAdd === true &&
                              renderAddActionWithClick(field?.id)}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  const isPinned = pinnedFieldIds?.some(
                    (items) =>
                      items?.fieldKey?.toLowerCase() ===
                        field?.id?.toLowerCase() ||
                      items?.fieldKey?.toLowerCase() ===
                        field?.label?.toLowerCase(),
                  );

                  const isEditing = editingFieldId === field?.id;
                  const isFieldCopied = copiedFieldId === field?.id;

                  // Determine if this field actually has a copyable value
                  const rawFieldText = getFieldValueForCopy(field);
                  const hasCopyableValue =
                    !!cleanCopiedText(rawFieldText) &&
                    rawFieldText !== '-' &&
                    rawFieldText !== 'Click to Add' &&
                    rawFieldText !== 'Click to add';

                  return (
                    <div
                      className="prospect-profile-section-fields"
                      key={field.id}
                    >
                      {field?.label && (
                        <span className="prospect-profile-section-fields-label">
                          <TruncatedTextWithAutoTooltip text={field?.label} />
                        </span>
                      )}
                      <div
                        className={`prospect-profile-section-field-details-container${
                          isPinned ? ' pinned' : ''
                        }${isEditing ? ' editing' : ''}`}
                        onMouseEnter={() => {
                          if (field?.id === 'email') {
                            dispatch(setEmailFieldHovered(true));
                          }
                        }}
                        onMouseLeave={() => {
                          if (field?.id === 'email') {
                            dispatch(setEmailFieldHovered(false));
                          }
                        }}
                      >
                        <div
                          className={`prospect-profile-section-field-detail${
                            field?.id === 'prospect-overview' &&
                            (!field?.value ||
                              getNodeText(field?.value ?? '').trim() === '' ||
                              getNodeText(field?.value ?? '').trim() === '-')
                              ? ' prospect-overview-empty'
                              : ''
                          }${field?.showEdit ? ' cursor-pointer' : ''}`}
                          onDoubleClick={() =>
                            handleFieldDoubleClick(field?.id, field?.showEdit)
                          }
                        >
                          {field?.valueNode}
                        </div>
                        <div className="prospect-profile-section-action-btn">
                          {field?.showAdd === true &&
                            renderAddActionWithClick(field?.id)}
                          {field?.showEdit && (
                            <OverlayTooltip text="Edit">
                              <span
                                role="button"
                                tabIndex={0}
                                className="prospect-profile-edit-action"
                                onClick={() => onEditClick(field?.id)}
                                onKeyDown={(event) =>
                                  onEditKeyDown(event, field?.id)
                                }
                              >
                                <Pencil
                                  width={16}
                                  height={16}
                                  className="prospect-profile-edit-icon"
                                />
                              </span>
                            </OverlayTooltip>
                          )}
                          {field?.showCopy && hasCopyableValue && (
                            <OverlayTooltip
                              text={isFieldCopied ? 'Copied' : 'Copy'}
                            >
                              <span
                                className="prospect-profile-copy"
                                {...accessibleOnClick(() =>
                                  handleCopyField(field),
                                )}
                              >
                                {isFieldCopied ? (
                                  <Check width={16} height={16} />
                                ) : (
                                  <Copy width={16} height={16} />
                                )}
                              </span>
                            </OverlayTooltip>
                          )}
                          {field?.showPin && (
                            <OverlayTooltip text={isPinned ? 'Unpin' : 'Pin'}>
                              <span
                                role="button"
                                tabIndex={0}
                                className="prospect-profile-pin-action"
                                onClick={() => onPinToggle(field?.id)}
                                onKeyDown={(event) =>
                                  onPinKeyDown(event, field?.id)
                                }
                              >
                                {isPinned ? (
                                  <PinBlue
                                    width={16}
                                    height={16}
                                    className="prospect-profile-pin pinned"
                                  />
                                ) : (
                                  <UnpinGrey
                                    width={16}
                                    height={16}
                                    className="prospect-profile-pin"
                                  />
                                )}
                              </span>
                            </OverlayTooltip>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))
      ) : (
        <ProspectProfileSectionSkeleton />
      )}
    </>
  );
};
